import React, {FC, useEffect, useState} from "react";
import HotelRoom from "../../modules/hotels/types/HotelRoom";
import ButtonPrimary from "../../modules/ui/shared/Button/ButtonPrimary";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import useBookingSearchContext from "../../modules/booking/hooks/useBookingSearchContext";
import BookingDates from "../../modules/booking/types/BookingDates";
import useCreatePrebooking from "../../modules/booking/hooks/useCreatePrebooking";
import NcInputNumber from "../../modules/ui/components/NcInputNumber/NcInputNumber";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import useAddRoom from "../../modules/booking/hooks/useAddRoom";
import HotelRoomFeedbackModal from "./HotelRoomFeedbackModal";
import {formatNumber} from "../../modules/common/utils/number";
import useCacheRemove from "../../modules/core/services/query/hooks/useCacheRemove";
import NcImage from "../../modules/ui/shared/NcImage/NcImage";
import ModalRoomImagesGallery from "../../modules/hotelEventRoom/components/ModalRoomImagesGallery";

export interface HotelRoomCardProps {
    key: number;
    hotelRoom: HotelRoom;
    eventCityId: any;
    roomPrice: number;
    currencySymbol?: string;
}

const HotelRoomCard: FC<HotelRoomCardProps> = ({
    hotelRoom,
    eventCityId,
    roomPrice,
    currencySymbol
}) => {
    const t = useTranslate();
    const {
        dates: bookDates,
        rooms,
        prebookingId,
        setPrebookingId
    } = useBookingSearchContext();

    const { languageId, languageCode } = useCustomerContext();
    const [quantity, setQuantity] = useState(1);
    const [price, setPrice] = useState(roomPrice);
    const [overbooking, setOverbooking] = useState(false);
    const [available, setAvailable] = useState(true);
    const [hotelAvailable, setHotelAvailable] = useState(true);
    const [roomAdded, setRoomAdded] = useState(false);
    const [unavailableDates, setUnavailableDates] = useState<BookingDates[]>([]);
    const [overbookingDates, setOverbookingDates] = useState<BookingDates[]>([]);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    const removeCache = useCacheRemove();

    const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
    const createPrebooking = useCreatePrebooking(languageId);
    const { save:addRoom, error:addRoomError, isLoading:isAddingRoomLoading } = useAddRoom();

    useEffect(() => {
        let totalCapacity = hotelRoom.adults + hotelRoom.children;
        let requestQuantity = 0;
        let suggestedQuantity = 0;
        // let roomPrice = totalPrice || 0;

        rooms.forEach((room) => {
            let totalChildren = room.guestChildren || 0;
            let totalAdults = room.guestAdults || 0;
            requestQuantity = totalChildren + totalAdults;

            if (requestQuantity === totalCapacity) {
                suggestedQuantity = suggestedQuantity + 1;
            }
        });

        if(suggestedQuantity === 0) {
            suggestedQuantity = 1;
        }

        setQuantity(suggestedQuantity);
        setPrice(roomPrice * suggestedQuantity);

    }, [rooms]);

    const handleAddRoom = async (allowOverbooking: boolean = false) => {
        setModalIsOpen(false);
        let prebookingEventualId = prebookingId;

        if(!prebookingEventualId) {
            const newPrebooking = await createPrebooking();
            setPrebookingId(newPrebooking.internalId);
            prebookingEventualId = newPrebooking.internalId;
        }

        addRoom({
            prebookingId: prebookingEventualId,
            prebooking: '/web/prebookings/' + prebookingEventualId,
            roomHotelEvent: '/web/room_hotel_events/' + hotelRoom.id,
            arrivalDate: bookDates.startDate?.format("YYYY-MM-DD"),
            departureDate: bookDates.endDate?.format("YYYY-MM-DD"),
            quantity: quantity,
            allowOverbooking: allowOverbooking
        }, {
            onSuccess: () => {
                setRoomAdded(true);
                setOverbooking(false);
                setAvailable(true);
                setUnavailableDates([]);
                setOverbookingDates([]);
                setModalIsOpen(true);
            },
            onError: (error: any) => {
                if (error.statusCode === 422) {
                    const prebookingHotelViolation = error.content.violations.find((v: any) => v.propertyPath === 'prebookingHotel');
                    const availableViolation = error.content.violations.find((v: any) => v.propertyPath === 'available');
                    if (prebookingHotelViolation !== undefined) {
                        setHotelAvailable(false);
                        setRoomAdded(false);
                        setModalIsOpen(true);
                        setIsCheckingAvailability(false);
                    } else if (availableViolation !== undefined) {
                        setAvailable(availableViolation.message === 'true');
                        const overbookingViolation = error.content.violations.find((v: any) => v.propertyPath === 'overbooking');
                        setOverbooking(overbookingViolation.message === 'true');
                        const unavailableDatesViolation = error.content.violations.find((v: any) => v.propertyPath === 'unavailableDates');
                        const arrayUnavailableDates = unavailableDatesViolation.message !== "" ?
                            unavailableDatesViolation.message.split(',').map((dateStr: string) => ({fecha: dateStr})) : [];
                        setUnavailableDates(arrayUnavailableDates);
                        const overbookingDatesViolation = error.content.violations.find((v: any) => v.propertyPath === 'overbookingDates');
                        const arrayOverbookingDates = overbookingDatesViolation.message !== "" ?
                            overbookingDatesViolation.message.split(',').map((dateStr: string) => ({fecha: dateStr})) : [];
                        setOverbookingDates(arrayOverbookingDates);

                        setRoomAdded(false);
                        setModalIsOpen(true);
                        setIsCheckingAvailability(false);
                    } else {
                        error.content.violations.forEach((v => {
                            setErrorMessage(v.message);
                        }));
                        setRoomAdded(false);
                        setOverbooking(false);
                        setAvailable(true);
                        setUnavailableDates([]);
                        setOverbookingDates([]);
                        setModalIsOpen(true);
                    }
                } else {
                    setRoomAdded(false);
                    setOverbooking(false);
                    setAvailable(true);
                    setUnavailableDates([]);
                    setOverbookingDates([]);
                    setModalIsOpen(true);
                }
            }
        });
    };

    const handleCheckAvailability = async () => {
        setIsCheckingAvailability(true);
        await handleAddRoom();
    }

    const handleConfirmAddRoom = async () => {
        await handleAddRoom(true);
    }

    const handleChangeQuantity = (quantity: number) => {
        // let roomPrice = totalPrice || 0;
        const totalPrice = roomPrice * quantity;
        setPrice(totalPrice);
        setQuantity(quantity);
        setAvailable(true);
    }

    let modalKind: "roomAdded" |
        "roomNotAvailable" |
        "roomAvailableWithOverbooking" |
        "hotelNotAvailable" |
        "error" |
        null = null;

    if (roomAdded) {
        modalKind = 'roomAdded';
    } else {
        if (!available) {
            modalKind = 'roomNotAvailable';
        } else if(overbooking) {
            modalKind = 'roomAvailableWithOverbooking';
        } else if(!hotelAvailable) {
            modalKind = 'hotelNotAvailable';
        } else if(addRoomError) {
            modalKind = 'error';
        }
    }

    // Map images to use principalImgContentUrl when contentUrl is null
    // This follows the same logic as HotelImagesGallery - simple and direct
    const roomImages = (hotelRoom.images || [])
        .filter(img => img.contentUrl !== null && img.contentUrl !== undefined)
        .length > 0
        ? (hotelRoom.images || []).filter(img => img.contentUrl !== null && img.contentUrl !== undefined)
        : (hotelRoom.principalImgContentUrl ? [{
            id: hotelRoom.id,
            hotel: "",
            hotelId: hotelRoom.id as any,
            contentUrl: hotelRoom.principalImgContentUrl,
            name: "Principal"
        }] : []);

    return (
        <div className="listingSectionSidebar__wrap shadow-xl mb-5">
            {/* TITLE */}
            <div className="flex justify-between mb-4">
                <span className="text-1xl font-semibold">
                    <i className="text-2xl las la-bed la-fw" style={{marginRight : "5px"}}></i> {hotelRoom.trType[languageCode]}
                </span>
            </div>

            {/* CONTENT WITH IMAGE - Use flex layout when image exists */}
            <div className="flex gap-8">
                {/* IMAGE */}
                {hotelRoom.principalImgContentUrl && (
                    <div
                        className="w-40 h-40 flex-shrink-0 rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => roomImages.length > 0 && setIsGalleryOpen(true)}
                    >
                        <NcImage
                            containerClassName="w-full h-full"
                            className="object-cover w-full h-full rounded-lg"
                            src={hotelRoom.principalImgContentUrl}
                        />
                    </div>
                )}

                {/* INFORMATION */}
                <div className="flex flex-col space-y-4 flex-1">
                    <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                        <span>{t("hotelDetail.from")}</span>
                        <span>{bookDates.startDate?.format("DD/MM/YYYY")}</span>
                    </div>
                    <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                        <span>{t("hotelDetail.to")}</span>
                        <span>{bookDates.endDate?.format("DD/MM/YYYY")}</span>
                    </div>
                    <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                        <span>{t("hotelDetail.capacity")}</span>
                        <span>
                            {[...Array(hotelRoom.adults)].map((value, index) =>
                                <i className="las la-user la-lg" key={index + hotelRoom.id}></i>
                            )}
                            {[...Array(hotelRoom.children)].map((value, index) =>
                                <i className="text-xl las la-user" key={index + hotelRoom.id}></i>
                            )}
                        </span>
                    </div>

                    <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
                        <span>Quantity:</span>
                        <NcInputNumber
                            className=""
                            defaultValue={quantity}
                            onChange={(value) => handleChangeQuantity(value)}
                            max={10}
                            min={1}
                        />
                    </div>

                    <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
                    <div className="flex justify-between font-semibold">
                        <span>{t("hotelDetail.total")}</span>
                        <div>{formatNumber(price)} <span dangerouslySetInnerHTML={{ __html:currencySymbol || '' }} /></div>
                    </div>
                </div>
            </div>

            {/* SUBMIT */}
            <ButtonPrimary href={`/event-city/${eventCityId}`} className="uppercase">
                <i className="text-2xl las la-calendar la-fw"></i> {t("hotelDetail.modifyDates")}
            </ButtonPrimary>

            <ButtonPrimary
                onClick={handleCheckAvailability}
                disabled={!available || isCheckingAvailability || isAddingRoomLoading}
                className="uppercase"
                loading={isCheckingAvailability || isAddingRoomLoading}
            >
                <i className="text-2xl las la-shopping-cart la-fw"></i> {t("hotelDetail.addRoom")}
            </ButtonPrimary>

            {modalKind && (
                <HotelRoomFeedbackModal
                    kind={modalKind}
                    isOpen={modalIsOpen}
                    onClose={() => {
                        setModalIsOpen(false);
                        setIsCheckingAvailability(false);
                        removeCache('basket-items');
                    }}
                    onConfirmAddRoom={handleConfirmAddRoom}
                    overbookingDates={overbookingDates}
                    unavailableDates={unavailableDates}
                    errorMessage={errorMessage}
                />
            )}

            {/* ROOM IMAGES GALLERY MODAL */}
            {roomImages.length > 0 && (
                <ModalRoomImagesGallery
                    roomImages={roomImages}
                    roomId={hotelRoom.id}
                    isOpen={isGalleryOpen}
                    onClose={() => setIsGalleryOpen(false)}
                    initFocus={0}
                    uniqueClassName="nc-HotelRoomCard-modalPhotos"
                />
            )}
        </div>
    );
};

export default HotelRoomCard;
